import type { BackendAdapter, BackendFilter, FilterValues } from "../../types";
import {
	pushBooleanFiltersIfSet,
	pushDateRangeFilters,
	pushExactFiltersIfSet,
	pushInArrayOrExactFilterIfSet,
	pushNumberRangeFilters,
	pushPercentageRangeFilters,
} from "../../types";
import type { ClubsRow } from "./columns";

/**
 * Backend adapter for Clubs tab.
 * Transforms filter values and sort config into the format expected by the API.
 */
export const clubsBackendAdapter: BackendAdapter<ClubsRow> = {
	/**
	 * Transform filter values into BackendFilter[] format.
	 * Only includes filters that have a value.
	 */
	buildFilters: (filterValues: FilterValues): BackendFilter[] => {
		const filters: BackendFilter[] = [];

		pushInArrayOrExactFilterIfSet(filters, "club", filterValues.club);
		pushInArrayOrExactFilterIfSet(filters, "manager", filterValues.manager);

		// Exact match filters
		if (filterValues.continent) {
			filters.push({
				field: "continent",
				type: "exact",
				value: filterValues.continent,
			});
		}

		pushInArrayOrExactFilterIfSet(filters, "country", filterValues.country);

		// Boolean filters
		pushBooleanFiltersIfSet(filters, filterValues, ["contract", "consent", "nonEbCamera", "marketingOptIn"]);

		pushExactFiltersIfSet(filters, filterValues, ["teamsheetAvailability", "fixtureAvailability"]);

		// Date range filters
		pushDateRangeFilters(filters, filterValues, ["onboardedDate", "lastPartnersUsage", "contractEndDate"]);

		// Number range filters (integer counts, no conversion)
		pushNumberRangeFilters(filters, filterValues, [
			"numberOfScoutTeams",
			"numberOfDiscoverTeams",
			"ntPlayers",
			"totalGames",
			"gamesTagged",
			"viewsOfGames",
		]);

		// Percentage filters (0-100 input → 0.0-1.0 for API)
		pushPercentageRangeFilters(filters, filterValues, [
			"consistentRunningPlayerPercent",
			"variablePlayerQualityPercent",
			"percentOfGamesTagged",
			"proportionOfGameViews",
		]);

		// Decimal/Average filters (no conversion)
		pushNumberRangeFilters(filters, filterValues, ["avgGamePriority", "avgGameAnalysisProcessing"]);

		return filters;
	},
};
